import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import io from 'socket.io-client';
import './style.css';
import { useToast } from '../ToastContainer';

export default function LiveStream({ onClose, isStreaming = false, streamData = null }) {
  const { user } = useSelector(state => state);
  const { showSuccess, showError } = useToast();
  const currentUserId = user?.id || user?._id;
  
  const [isLive, setIsLive] = useState(isStreaming);
  const [title, setTitle] = useState(streamData?.title || '');
  const [description, setDescription] = useState(streamData?.description || '');
  const [visibility, setVisibility] = useState('followers');
  const [allowComments, setAllowComments] = useState(true);
  const [currentStream, setCurrentStream] = useState(streamData);
  const [viewerCount, setViewerCount] = useState(streamData?.viewerCount || 0);
  const [loading, setLoading] = useState(false);
  const [streamStartTime, setStreamStartTime] = useState(null);
  const [duration, setDuration] = useState('00:00:00');
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [initializingCamera, setInitializingCamera] = useState(true);
  const [liveComments, setLiveComments] = useState(Array.isArray(streamData?.comments) ? streamData.comments : []);

  const [showEndConfirmation, setShowEndConfirmation] = useState(false);
  
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const isMountedRef = useRef(true);
  const isLiveRef = useRef(isStreaming);
  const suppressDisconnectToastRef = useRef(false);
  const socketRef = useRef(null);
  const intentionalDisconnectRef = useRef(false);
  const peerConnections = useRef(new Map()); // Store peer connections for each viewer
  const creatingPeerConnections = useRef(new Set());
  const pendingViewerRequests = useRef([]); // Store pending viewer requests when camera not ready
  const pendingIceCandidatesByViewer = useRef(new Map());
  const emitBroadcasterReady = useCallback((streamId) => {
    if (!streamId || !socketRef.current || !socketRef.current.connected || !streamRef.current) {
      return;
    }
    console.log('📡 Emitting broadcaster-ready for stream:', streamId);
    socketRef.current.emit('broadcaster-ready', { streamId });
  }, []);

  const drainViewerIceQueue = async (viewerId, peerConnection) => {
    if (!peerConnection?.remoteDescription) return;
    const queue = pendingIceCandidatesByViewer.current.get(viewerId) || [];
    pendingIceCandidatesByViewer.current.set(viewerId, []);
    for (const candidateInit of queue) {
      try {
        await peerConnection.addIceCandidate(new RTCIceCandidate(candidateInit));
      } catch (err) {
        console.warn(`Queued ICE candidate failed for viewer ${viewerId}:`, err?.message || err);
      }
    }
  };

  const addViewerIceCandidate = async (viewerId, peerConnection, candidateInit) => {
    if (!peerConnection || candidateInit == null) return;
    if (!peerConnection.remoteDescription) {
      const queue = pendingIceCandidatesByViewer.current.get(viewerId) || [];
      queue.push(candidateInit);
      pendingIceCandidatesByViewer.current.set(viewerId, queue);
      return;
    }
    try {
      await peerConnection.addIceCandidate(new RTCIceCandidate(candidateInit));
    } catch (err) {
      console.warn(`ICE candidate add failed for viewer ${viewerId}:`, err?.message || err);
    }
  };

  const formatCommentTime = (createdAt) => {
    if (!createdAt) return '';
    const date = new Date(createdAt);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };
  useEffect(() => {
    isLiveRef.current = isLive;
  }, [isLive]);
  
  // Debug function to force video refresh
  const forceVideoRefresh = () => {
    if (videoRef.current && streamRef.current) {
      console.log('Force refreshing video display...');
      videoRef.current.srcObject = null;
      setTimeout(() => {
        videoRef.current.srcObject = streamRef.current;
        videoRef.current.play().catch(e => console.log('Force play failed:', e));
      }, 100);
    }
  };
  
  // System camera test function
  const runCameraDiagnostic = async () => {
    console.log('=== CAMERA DIAGNOSTIC START ===');
    
    try {
      // Test 1: Check if MediaDevices API is supported
      console.log('1. MediaDevices API support:', !!navigator.mediaDevices);
      console.log('2. getUserMedia support:', !!navigator.mediaDevices?.getUserMedia);
      
      // Test 2: Check available devices
      if (navigator.mediaDevices?.enumerateDevices) {
        try {
          const devices = await navigator.mediaDevices.enumerateDevices();
          console.log('3. Available devices:', devices);
          console.log('   - Video inputs:', devices.filter(d => d.kind === 'videoinput').length);
          console.log('   - Audio inputs:', devices.filter(d => d.kind === 'audioinput').length);
        } catch (e) {
          console.log('3. Device enumeration failed:', e);
        }
      }
      
      // Test 3: Check permissions
      if (navigator.permissions?.query) {
        try {
          const cameraPermission = await navigator.permissions.query({ name: 'camera' });
          const microphonePermission = await navigator.permissions.query({ name: 'microphone' });
          console.log('4. Permissions:');
          console.log('   - Camera:', cameraPermission.state);
          console.log('   - Microphone:', microphonePermission.state);
        } catch (e) {
          console.log('4. Permission check failed:', e);
        }
      }
      
      // Test 4: Try basic camera access
      try {
        console.log('5. Testing basic camera access...');
        const testStream = await navigator.mediaDevices.getUserMedia({ video: true });
        console.log('5. Basic camera access: SUCCESS');
        console.log('   - Stream active:', testStream.active);
        console.log('   - Video tracks:', testStream.getVideoTracks().length);
        testStream.getTracks().forEach(track => track.stop());
      } catch (e) {
        console.log('5. Basic camera access: FAILED', e);
      }
      
    } catch (error) {
      console.log('Diagnostic failed:', error);
    }
    
    console.log('=== CAMERA DIAGNOSTIC END ===');
  };
  
  // Monitor video status
  useEffect(() => {
    if (cameraReady && videoRef.current) {
      const interval = setInterval(() => {
        if (videoRef.current) {
          // Video info available but not used
  
        }
      }, 1000);
      
      return () => clearInterval(interval);
    }
  }, [cameraReady]);
  
  // Timer for updating duration
  useEffect(() => {
    let interval;
    if (isLive && streamStartTime) {
      interval = setInterval(() => {
        const now = new Date();
        const diff = Math.floor((now - streamStartTime) / 1000);
        const hours = Math.floor(diff / 3600).toString().padStart(2, '0');
        const minutes = Math.floor((diff % 3600) / 60).toString().padStart(2, '0');
        const seconds = (diff % 60).toString().padStart(2, '0');
        setDuration(`${hours}:${minutes}:${seconds}`);
      }, 1000);
    }
    return () => interval && clearInterval(interval);
  }, [isLive, streamStartTime]);
  
    // Initialize camera with better error handling and fallback options
  const initializeCamera = async () => {
    if (!isMountedRef.current) return;
    
    setInitializingCamera(true);
    setCameraError(null);
    
    try {
      console.log('📹 Starting camera initialization...');
      
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Browser does not support camera access');
      }

      // Stop any existing streams first
      if (streamRef.current) {
        console.log('🛑 Stopping existing stream...');
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }

      // First, check what devices are available
      try {
        console.log('🔍 Checking available devices...');
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(device => device.kind === 'videoinput');
        const audioDevices = devices.filter(device => device.kind === 'audioinput');
        
        console.log('📊 Available devices:', {
          totalDevices: devices.length,
          videoDevices: videoDevices.length,
          audioDevices: audioDevices.length,
          videoDevicesList: videoDevices.map(d => ({ label: d.label, deviceId: d.deviceId })),
          audioDevicesList: audioDevices.map(d => ({ label: d.label, deviceId: d.deviceId }))
        });
        
        if (videoDevices.length === 0 && audioDevices.length === 0) {
          throw new Error('No camera or microphone devices found. Please connect a camera and refresh the page.');
        }
        
        if (videoDevices.length === 0) {
          throw new Error('No camera devices found. Please connect a camera or check if another application is using it.');
        }
        
      } catch (deviceError) {
        console.error('❌ Error checking devices:', deviceError);
        // Continue anyway, sometimes enumerateDevices fails but getUserMedia works
      }

      // Check if we have permissions
      try {
        const permissions = await navigator.permissions.query({ name: 'camera' });
        console.log('🔐 Camera permission status:', permissions.state);
        
        if (permissions.state === 'denied') {
          throw new Error('Camera permission is denied. Please enable camera access in your browser settings and refresh the page.');
        }
      } catch (permError) {
        console.log('⚠️ Permission query not supported, continuing...', permError);
      }

      // Try different constraint combinations for better compatibility
      const constraintOptions = [
        // Option 1: Basic video and audio
        { video: true, audio: true },
        // Option 2: User-facing camera with audio
        { video: { facingMode: 'user' }, audio: true },
        // Option 3: High quality video and audio
        {
          video: {
            width: { ideal: 1280 },
            height: { ideal: 720 },
            facingMode: 'user'
          },
          audio: true
        },
        // Option 4: Fallback video-only if microphone is unavailable
        { video: true, audio: false }
      ];

      let stream = null;
      let lastError = null;

      for (let i = 0; i < constraintOptions.length; i++) {
        try {
          console.log(`🔧 Trying constraint option ${i + 1}:`, constraintOptions[i]);
          
          // Check if component is still mounted before proceeding
          if (!isMountedRef.current) {
            console.log('⚠️ Component unmounted, aborting camera initialization');
            return;
          }
          
          // Add small delay to prevent rapid successive requests
          if (i > 0) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
          
          // Check again after delay
          if (!isMountedRef.current) {
            console.log('⚠️ Component unmounted during delay, aborting camera initialization');
            return;
          }
          
          stream = await navigator.mediaDevices.getUserMedia(constraintOptions[i]);
          console.log('✅ Successfully got stream with option', i + 1);
          break;
        } catch (error) {
          console.log(`❌ Option ${i + 1} failed:`, error);
          lastError = error;
          
          // If it's a permission error, don't try other options
          if (error.name === 'NotAllowedError') {
            break;
          }
          
          // If it's an AbortError, wait a bit before trying next option
          if (error.name === 'AbortError') {
            console.log('⏸️ Camera access was aborted, waiting before retry...');
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }
      }

      if (!stream) {
        throw lastError || new Error('All camera access attempts failed');
      }
      
      if (videoRef.current && stream && isMountedRef.current) {
        console.log('🎥 Setting up video element...');
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        
        // Handle video loading and playing
        const handleVideoReady = () => {
          if (isMountedRef.current) {
            console.log('✅ Video is ready to play');
            
            // Additional debugging for video element
            if (videoRef.current) {
              console.log('📊 Video element state:', {
                videoWidth: videoRef.current.videoWidth,
                videoHeight: videoRef.current.videoHeight,
                readyState: videoRef.current.readyState,
                paused: videoRef.current.paused,
                srcObject: !!videoRef.current.srcObject,
                currentTime: videoRef.current.currentTime
              });
            }
            
            setCameraReady(true);
            setInitializingCamera(false);
            setCameraError(null);
            
            const videoTracks = stream.getVideoTracks();
            const audioTracks = stream.getAudioTracks();
            console.log('📊 Stream info:', {
              video: videoTracks.length,
              audio: audioTracks.length,
              videoSettings: videoTracks[0]?.getSettings(),
              streamActive: stream.active,
              streamId: stream.id
            });
            
            if (videoTracks.length > 0 && audioTracks.length > 0) {
              showSuccess('Camera and microphone ready!');
            } else if (videoTracks.length > 0) {
              showSuccess('Camera ready! (No microphone access)');
            } else {
              showSuccess('Audio ready! (No camera access)');
            }
          }
        };

        // Set up event listeners
        videoRef.current.onloadedmetadata = () => {
          console.log('📹 Video metadata loaded');
          handleVideoReady();
        };
        
        videoRef.current.oncanplay = () => {
          console.log('▶️ Video can play');
          if (!cameraReady) {
            handleVideoReady();
          }
        };
        
        videoRef.current.onloadeddata = () => {
          console.log('📊 Video data loaded');
        };
        
        videoRef.current.onplay = () => {
          console.log('▶️ Video started playing');
        };
        
        // Force the video element to be visible
        videoRef.current.style.display = 'block';
        videoRef.current.style.visibility = 'visible';
        
        // Try to play immediately
        setTimeout(() => {
          if (videoRef.current && isMountedRef.current) {
            videoRef.current.play().catch(e => {
              console.log('⚠️ Auto-play failed:', e);
              // Even if autoplay fails, the video is still ready
              if (!cameraReady) {
                handleVideoReady();
              }
            });
          }
        }, 100);
        
        // Fallback: Mark as ready after a short delay
        setTimeout(() => {
          if (isMountedRef.current && !cameraReady && stream && stream.active) {
            console.log('⏰ Fallback: marking camera as ready after timeout');
            handleVideoReady();
          }
        }, 2000);
      }
      
      if (isMountedRef.current) {
        setInitializingCamera(false);
      }
    } catch (error) {
      console.error('❌ Error accessing camera:', error);
      if (isMountedRef.current) {
        setInitializingCamera(false);
        
        let errorMessage = '';
        
        switch (error.name) {
          case 'NotAllowedError':
            errorMessage = 'Camera access denied. Please click "Allow" when prompted, or enable camera permissions in your browser settings.';
            break;
          case 'NotFoundError':
            errorMessage = 'No camera found on your device. Please check:\n• Is a camera connected to your computer?\n• Close other apps that might be using the camera (Zoom, Teams, Skype, etc.)\n• Try refreshing the page\n• Check your camera in system settings';
            break;
          case 'NotReadableError':
            errorMessage = 'Camera is being used by another application. Please close other apps using the camera (Zoom, Teams, Skype, etc.) and try again.';
            break;
          case 'OverconstrainedError':
            errorMessage = 'Camera settings not supported. Trying basic settings...';
            break;
          case 'SecurityError':
            errorMessage = 'Camera access blocked for security reasons. Please enable camera access for this website.';
            break;
          case 'AbortError':
            errorMessage = 'Camera access was interrupted. This can happen if:\n• Another app is using your camera (close Zoom, Teams, Skype, etc.)\n• Browser security settings blocked the request\n• Page was refreshed during camera setup\n\nPlease try again or refresh the page.';
            break;
          default:
            errorMessage = error.message || `Camera access failed: ${error.name || 'Unknown error'}`;
        }
        
        setCameraError(errorMessage);
        showError(errorMessage);
      }
    }
  };
  
  // Start live stream
  const startLiveStream = async () => {
    if (!title.trim()) {
      showError('Please enter a stream title');
      return;
    }
    
    setLoading(true);
    try {
      console.log('Starting live stream with data:', {
        title: title.trim(),
        description: description.trim(),
        settings: {
          visibility,
          allowComments,
        }
      });
      
      // console.log('User token:', user.token ? 'Present' : 'Missing');
      // console.log('User ID:', user.id);
      
      const response = await axios.post('/livestream/start', {
        title: title.trim(),
        description: description.trim(),
        settings: {
          visibility,
          allowComments,
        }
      }, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      
      console.log('Live stream start response:', response.data);
      
      if (response.data.success) {
        setCurrentStream(response.data.liveStream);
        setLiveComments([]);
        setIsLive(true);
        setStreamStartTime(new Date());
        setViewerCount(0);
        showSuccess('Live stream started successfully!');
        
        // Initialize camera after starting stream (if not already initialized)
        if (!cameraReady) {
          await initializeCamera();
        }
        
        // Start broadcasting to viewers
        await initializeBroadcast(response.data.liveStream._id);
      }
    } catch (error) {
      console.error('Error starting live stream:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      showError(error.response?.data?.message || 'Failed to start live stream');
    }
    setLoading(false);
  };

  // Helper to join stream as broadcaster
  const joinStreamAsBroadcaster = (streamId) => {
    if (socketRef.current && socketRef.current.connected) {
      if (!currentUserId) {
        showError('Unable to identify broadcaster user. Please re-login and try again.');
        return;
      }

      console.log('Emitting join-stream as broadcaster for stream:', streamId);
      socketRef.current.emit('join-stream', {
        streamId,
        userId: String(currentUserId),
        role: 'broadcaster'
      });
    } else {
      console.warn('Socket not connected, cannot join as broadcaster');
    }
  };

  // Initialize broadcasting to viewers
  const initializeBroadcast = async (streamId) => {
    try {
      suppressDisconnectToastRef.current = false;
      console.log('🔌 Initializing broadcast for stream:', streamId);
      
      // Always use the existing notification socket if available
      let socket = null;
      if (window.notificationSocket && window.notificationSocket.connected) {
        socket = window.notificationSocket;
        console.log('✅ Using existing notification socket for broadcasting');
      } else {
        console.log('⚠️ No existing socket, creating new connection...');
        // Auto-detect backend URL: use env var if set, otherwise detect from current location
        let socketURL = process.env.REACT_APP_BACKEND_URL;
        
        if (!socketURL || socketURL === 'http://localhost:8000') {
          // In production, use same origin (relative URL) or detect from window
          if (typeof window !== 'undefined') {
            const hostname = window.location.hostname;
            
            // Check if it's a production domain (not localhost)
            if (hostname !== 'localhost' && hostname !== '127.0.0.1' && !hostname.startsWith('192.168.')) {
              // Production: use same origin for socket (socket.io will handle it automatically)
              socketURL = '';
              // console.log(`🌐 Production domain detected: ${hostname}, using same origin for socket`);
            } else {
              // Development: use localhost:8000
              socketURL = 'http://localhost:8000';
              // console.log(`💻 Development mode: using ${socketURL} for socket`);
            }
          } else {
            socketURL = 'http://localhost:8000';
          }
        }
        
        socket = io(socketURL || undefined, {
          withCredentials: true,
          transports: ['websocket', 'polling'],
          timeout: 20000,
          forceNew: false, // Changed to false to prevent multiple connections
          reconnection: true,
          reconnectionAttempts: 5,
          reconnectionDelay: 1000
        });
        
        // Wait for connection
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error('Socket connection timeout'));
          }, 10000);
          
          socket.on('connect', () => {
            clearTimeout(timeout);
            console.log('✅ New socket connected for broadcasting:', socket.id);
            resolve();
          });
          
          socket.on('connect_error', (error) => {
            clearTimeout(timeout);
            reject(error);
          });
        });
      }
      
      socketRef.current = socket;

      // Robust connection logging
      socket.on('connect', () => {
        intentionalDisconnectRef.current = false;
        console.log('🔌 Broadcaster socket connected:', socket.id);
        joinStreamAsBroadcaster(streamId);
        emitBroadcasterReady(streamId);
      });
      
      socket.on('disconnect', (reason) => {
        console.log('❌ Broadcaster socket disconnected:', reason);
        const isIntentional = intentionalDisconnectRef.current || reason === 'io client disconnect';
        if (isIntentional) {
          console.log('ℹ️ Skipping disconnect toast for intentional broadcaster disconnect');
          intentionalDisconnectRef.current = false;
          return;
        }
        showError('Broadcast connection lost. Please check your internet connection.');
      });
      
      socket.on('connect_error', (error) => {
        console.error('❌ Socket connection error:', error);
        showError('Connection error. Please check your internet connection.');
      });

      // Always join as broadcaster if already connected
      if (socket.connected) {
        joinStreamAsBroadcaster(streamId);
        emitBroadcasterReady(streamId);
      }

      socket.off('live-stream-comment');
      socket.on('live-stream-comment', (data) => {
        if (!data || data.streamId !== streamId || !data.comment) return;

        setLiveComments((prev) => {
          if (prev.some((c) => String(c._id) === String(data.comment._id))) {
            return prev;
          }
          return [...prev, data.comment];
        });
      });

      // Handle viewer joining - create peer connection
      socket.on('viewer-joined', async (data) => {
        console.log('👤 Viewer joined:', data.viewerId);
        if (typeof data.viewerCount === 'number') {
          setViewerCount(data.viewerCount);
        } else {
          setViewerCount(prev => prev + 1);
        }

        // Only create peer connection if camera is ready
        if (cameraReady && streamRef.current) {
          await createPeerConnection(data.viewerId, streamId);
        } else {
          console.log('⚠️ Camera not ready yet, will create connection when ready');
          // Store pending viewer request
          if (!pendingViewerRequests.current) {
            pendingViewerRequests.current = [];
          }
          pendingViewerRequests.current.push(data.viewerId);
        }
      });
      
      socket.on('request-stream', async (data) => {
        console.log('📡 Stream requested by viewer:', data.viewerId);
        // Only create peer connection if camera is ready
        if (cameraReady && streamRef.current) {
          await createPeerConnection(data.viewerId, streamId);
        } else {
          console.log('⚠️ Camera not ready yet, will create connection when ready');
          // Store pending viewer request
          if (!pendingViewerRequests.current) {
            pendingViewerRequests.current = [];
          }
          pendingViewerRequests.current.push(data.viewerId);
        }
      });
      
      socket.on('stream-answer', async (data) => {
        console.log('📡 Received answer from viewer:', data.viewerId);
        try {
          const peerConnection = peerConnections.current.get(data.viewerId);
          if (peerConnection) {
            if (peerConnection.signalingState !== 'have-local-offer') {
              console.warn(
                `⚠️ Ignoring viewer answer in signaling state ${peerConnection.signalingState} for viewer ${data.viewerId}`
              );
              return;
            }
            await peerConnection.setRemoteDescription(data.answer);
            await drainViewerIceQueue(data.viewerId, peerConnection);
            console.log('✅ Remote description set for viewer:', data.viewerId);
          }
        } catch (error) {
          console.error('❌ Error setting remote description:', error);
        }
      });
      
      socket.on('ice-candidate', async (data) => {
        const peerConnection = peerConnections.current.get(data.viewerId);
        if (peerConnection && data.candidate) {
          await addViewerIceCandidate(data.viewerId, peerConnection, data.candidate);
        }
      });
      
      // Handle viewer leaving
      socket.on('viewer-left', (data) => {
        console.log('👤 Viewer left:', data.viewerId);
        if (typeof data.viewerCount === 'number') {
          setViewerCount(data.viewerCount);
        } else {
          setViewerCount(prev => Math.max(0, prev - 1));
        }

        const peerConnection = peerConnections.current.get(data.viewerId);
        if (peerConnection) {
          peerConnection.close();
          peerConnections.current.delete(data.viewerId);
        }
        pendingIceCandidatesByViewer.current.delete(data.viewerId);
      });
      
    } catch (error) {
      console.error('❌ Error initializing broadcast:', error);
      showError('Failed to initialize broadcast connection: ' + error.message);
    }
  };

  // Create peer connection for a viewer
  const createPeerConnection = useCallback(async (viewerId, streamId) => {
    try {
      if (creatingPeerConnections.current.has(viewerId)) {
        console.log('⚠️ Peer connection creation already in progress for viewer:', viewerId);
        return;
      }
      creatingPeerConnections.current.add(viewerId);

      // Check if camera is ready
      if (!cameraReady || !streamRef.current) {
        console.error('❌ Cannot create peer connection: camera not ready');
        return;
      }

      // Check if stream has tracks
      const tracks = streamRef.current.getTracks();
      if (tracks.length === 0) {
        console.error('❌ Cannot create peer connection: no tracks in stream');
        return;
      }

      // Check if peer connection already exists for this viewer
      if (peerConnections.current.has(viewerId)) {
        console.log('⚠️ Peer connection already exists for viewer:', viewerId);
        const existingPC = peerConnections.current.get(viewerId);
        if (existingPC.connectionState !== 'closed' && existingPC.connectionState !== 'failed') {
          return; // Connection already exists and is active
        } else {
          // Clean up failed connection
          existingPC.close();
          peerConnections.current.delete(viewerId);
        }
      }

      console.log('🔗 Creating peer connection for viewer:', viewerId);
      console.log('📊 Stream tracks:', tracks.length, tracks.map(t => ({ kind: t.kind, enabled: t.enabled, readyState: t.readyState })));

      // Create new peer connection with better STUN configuration
      const peerConnection = new RTCPeerConnection({
        iceServers: [
          { urls: 'stun:stun.l.google.com:19302' },
          { urls: 'stun:stun1.l.google.com:19302' },
          { urls: 'stun:stun2.l.google.com:19302' },
          { urls: 'stun:stun3.l.google.com:19302' },
          { urls: 'stun:stun4.l.google.com:19302' },
          // Add some additional STUN servers for better connectivity
          { urls: 'stun:stun.stunprotocol.org:3478' },
          { urls: 'stun:stun.voiparound.com' },
          { urls: 'stun:stun.voipbuster.com' }
        ],
        iceCandidatePoolSize: 10,
        iceTransportPolicy: 'all', // Try both relay and direct connections
        bundlePolicy: 'max-bundle',
        rtcpMuxPolicy: 'require'
      });

      // Add local stream tracks to peer connection
      tracks.forEach(track => {
        if (track.readyState === 'live') {
          peerConnection.addTrack(track, streamRef.current);
          console.log('✅ Added track:', track.kind, track.id);
        } else {
          console.warn('⚠️ Track not live:', track.kind, track.readyState);
        }
      });

      // Verify tracks were added
      const senders = peerConnection.getSenders();
      console.log('📤 Peer connection senders:', senders.length);
      if (senders.length === 0) {
        throw new Error('No tracks added to peer connection');
      }

      // Handle connection state changes
      peerConnection.onconnectionstatechange = () => {
        console.log(`🔗 Peer connection state for viewer ${viewerId}:`, peerConnection.connectionState);
        if (peerConnection.connectionState === 'connected') {
          console.log(`✅ Peer connection established for viewer ${viewerId}`);
        } else if (peerConnection.connectionState === 'failed') {
          console.error(`❌ Peer connection failed for viewer ${viewerId}`);
          // Try to restart ICE
          try {
            peerConnection.restartIce();
            console.log(`🔄 Restarting ICE for viewer ${viewerId}`);
            
            // If restart doesn't work, recreate the connection after a delay
            setTimeout(() => {
              if (peerConnection.connectionState === 'failed') {
                console.log(`🔄 Recreating peer connection for viewer ${viewerId}`);
                // Close and remove the failed connection
                peerConnection.close();
                peerConnections.current.delete(viewerId);
                
                // Recreate if camera is still ready
                if (cameraReady && streamRef.current && socketRef.current && socketRef.current.connected) {
                  setTimeout(() => {
                    createPeerConnection(viewerId, streamId);
                  }, 1000);
                }
              }
            }, 5000);
          } catch (error) {
            console.error(`Error restarting ICE for viewer ${viewerId}:`, error);
          }
        } else if (peerConnection.connectionState === 'disconnected') {
          console.log(`⚠️ Peer connection disconnected for viewer ${viewerId}`);
          // Wait a bit to see if it reconnects
          setTimeout(() => {
            if (peerConnection.connectionState === 'disconnected') {
              console.log(`🔄 Recreating peer connection for viewer ${viewerId} after disconnect`);
              peerConnection.close();
              peerConnections.current.delete(viewerId);
              
              // Recreate if camera is still ready
              if (cameraReady && streamRef.current && socketRef.current && socketRef.current.connected) {
                setTimeout(() => {
                  createPeerConnection(viewerId, streamId);
                }, 1000);
              }
            }
          }, 5000);
        }
      };

      // Handle ICE connection state changes
      peerConnection.oniceconnectionstatechange = () => {
        console.log(`🧊 ICE connection state for viewer ${viewerId}:`, peerConnection.iceConnectionState);
        if (peerConnection.iceConnectionState === 'failed') {
          console.error(`❌ ICE connection failed for viewer ${viewerId}`);
          // Try to restart ICE
          try {
            peerConnection.restartIce();
            console.log(`🔄 Restarting ICE for viewer ${viewerId}`);
            
            // Check if restart worked after a delay
            setTimeout(() => {
              if (peerConnection.iceConnectionState === 'failed') {
                console.log(`❌ ICE restart did not recover for viewer ${viewerId}`);
                // Connection state handler will handle recreation
              }
            }, 3000);
          } catch (error) {
            console.error(`Error restarting ICE for viewer ${viewerId}:`, error);
          }
        } else if (peerConnection.iceConnectionState === 'connected') {
          console.log(`✅ ICE connection established for viewer ${viewerId}`);
        } else if (peerConnection.iceConnectionState === 'checking') {
          console.log(`🔄 ICE connection checking for viewer ${viewerId}...`);
        }
      };

      // Handle ICE candidates
      peerConnection.onicecandidate = (event) => {
        if (event.candidate && socketRef.current) {
          console.log('🧊 Sending ICE candidate to viewer:', viewerId);
          socketRef.current.emit('ice-candidate', {
            candidate: event.candidate,
            streamId,
            viewerId,
            type: 'broadcaster'
          });
        } else if (!event.candidate) {
          console.log('🧊 ICE gathering completed for viewer:', viewerId);
        }
      };

      // Send offer immediately (trickle ICE). Viewers queue candidates until
      // the remote description is set so desktop does not hit InvalidStateError.
      const offer = await peerConnection.createOffer({
        offerToReceiveAudio: false,
        offerToReceiveVideo: false
      });
      await peerConnection.setLocalDescription(offer);

      peerConnections.current.set(viewerId, peerConnection);
      pendingIceCandidatesByViewer.current.set(viewerId, []);

      // Send offer to viewer
      if (socketRef.current && socketRef.current.connected) {
        socketRef.current.emit('stream-offer', {
          offer: peerConnection.localDescription || offer,
          streamId,
          viewerId
        });
        console.log('✅ Offer sent to viewer:', viewerId);
      } else {
        console.error('❌ Socket not connected, cannot send offer');
        peerConnection.close();
        peerConnections.current.delete(viewerId);
      }

    } catch (error) {
      console.error('❌ Error creating peer connection:', error);
      // Clean up on error
      const peerConnection = peerConnections.current.get(viewerId);
      if (peerConnection) {
        peerConnection.close();
        peerConnections.current.delete(viewerId);
        pendingIceCandidatesByViewer.current.delete(viewerId);
      }
    } finally {
      creatingPeerConnections.current.delete(viewerId);
    }
  }, [cameraReady]);
  
  // Show end stream confirmation
  const showEndConfirmationDialog = () => {
    setShowEndConfirmation(true);
  };

  // Stop live stream
  const stopLiveStream = async () => {
    if (!currentStream?._id) return;
    
    setLoading(true);
    try {
      const response = await axios.put(`/livestream/stop/${currentStream._id}`, {}, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      
      if (response.data.success) {
        setIsLive(false);
        setCurrentStream(null);
        setStreamStartTime(null);
        setViewerCount(0);
        setDuration('00:00:00');
        
        // Clean up broadcasting connections
        if (socketRef.current) {
          intentionalDisconnectRef.current = true;
          socketRef.current.emit('stream-ended', { streamId: currentStream._id });
          socketRef.current.disconnect();
          socketRef.current = null;
        }
        
        // Close all peer connections
        peerConnections.current.forEach(peerConnection => {
          peerConnection.close();
        });
        peerConnections.current.clear();
        
        // Stop camera stream
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }
        if (videoRef.current) {
          videoRef.current.srcObject = null;
        }
        setCameraReady(false);
        
        showSuccess(`Live stream ended. Duration: ${response.data.duration || 0} seconds`);
        onClose();
      }
    } catch (error) {
      console.error('Error stopping live stream:', error);
      showError('Failed to stop live stream');
    }
    setLoading(false);
    setShowEndConfirmation(false);
  };
  
  // Check and stop any existing active streams
  const checkAndStopExistingStreams = async () => {
    try {
      const response = await axios.get('/livestream/my-streams', {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      
      if (response.data.success && response.data.streams) {
        const activeStream = response.data.streams.find(stream => stream.isActive);
        if (activeStream) {
          console.log('Found existing active stream:', activeStream._id);
          await stopSpecificStream(activeStream._id);
        }
      }
    } catch (error) {
      console.error('Error checking existing streams:', error);
    }
  };

  // Stop a specific stream by ID
  const stopSpecificStream = async (streamId) => {
    try {
      const response = await axios.put(`/livestream/stop/${streamId}`, {}, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      
      if (response.data.success) {
        console.log('Stopped existing stream:', streamId);
        showSuccess('Previous live stream ended successfully');
        return true;
      }
    } catch (error) {
      console.error('Error stopping stream:', error);
      showError('Failed to stop existing stream');
      return false;
    }
  };

  // Initialize camera on mount and cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    
    // Check and stop any existing active streams first
    checkAndStopExistingStreams();
    
    // Initialize camera immediately
    initializeCamera();
    
    // If this is for an existing stream, set up stream data
    if (isStreaming && streamData) {
      setCurrentStream(streamData);
      setStreamStartTime(new Date(streamData.startedAt));
    }
    
    const peerConnectionsMap = peerConnections.current;
    const stream = streamRef.current;
    const videoElement = videoRef.current;
    return () => {
      isMountedRef.current = false;
      
      // Clean up broadcasting connections
      if (socketRef.current) {
        intentionalDisconnectRef.current = true;
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      
      // Close all peer connections
      peerConnectionsMap.forEach(peerConnection => {
        peerConnection.close();
      });
      peerConnectionsMap.clear();
      
      // Cleanup camera on unmount
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      if (videoElement) {
        videoElement.srcObject = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ensure broadcaster emits ready as soon as camera is ready and handle pending viewer requests
  useEffect(() => {
    if (isLive && cameraReady && currentStream?._id && socketRef.current && socketRef.current.connected) {
      emitBroadcasterReady(currentStream._id);
      
      // Process any pending viewer requests
      if (pendingViewerRequests.current && pendingViewerRequests.current.length > 0) {
        console.log('📡 Processing pending viewer requests:', pendingViewerRequests.current.length);
        const pendingViewers = [...pendingViewerRequests.current];
        pendingViewerRequests.current = [];
        
        pendingViewers.forEach(viewerId => {
          createPeerConnection(viewerId, currentStream._id);
        });
      }
    }
  }, [isLive, cameraReady, currentStream?._id, createPeerConnection, emitBroadcasterReady]);
  
  return (
    <div className="live_stream_overlay">
      <div className="live_stream_modal">
        <div className="live_stream_header">
          <div className="live_stream_title">
            {isLive ? (
              <span className="live_indicator">
                🔴 LIVE - {duration}
              </span>
            ) : (
              'Start Live Stream'
            )}
          </div>
          {/* Show close (✕) only on the setup screen. Once the broadcast is
              live, the bottom `⏹️ End Stream` button is the single, explicit
              way to terminate — avoids a duplicate/confusing action and
              prevents accidental "close-but-still-streaming" state. */}
          {!isLive && (
            <button className="close_btn" onClick={onClose} title="Close">
              ✕
            </button>
          )}
        </div>
        
        <div className="live_stream_content">
          {/* Video Preview/Stream */}
          <div className="video_container">
            <div style={{ position: 'relative', width: '100%', height: '100%' }}>
              {/* Always show video element, but control visibility */}
              <video
                ref={videoRef}
                autoPlay
                muted
                playsInline
                className="stream_video"
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  objectFit: 'cover',
                  backgroundColor: '#000',
                  display: cameraReady ? 'block' : 'none'
                }}
              />
              
                             {/* Show placeholder only when camera is not ready */}
               {!cameraReady && (
                 <div className="camera_placeholder" style={{
                   position: 'absolute',
                   top: 0,
                   left: 0,
                   right: 0,
                   bottom: 0,
                   zIndex: 2
                 }}>
                   <div className="placeholder_content">
                     <div className="camera_icon">📹</div>
                     {initializingCamera ? (
                       <>
                         <p>Requesting camera access...</p>
                         <div className="loading_spinner">⏳</div>
                         <p style={{fontSize: '12px', opacity: 0.7, marginTop: '10px'}}>
                           Please click "Allow" when prompted by your browser
                         </p>
                       </>
                     ) : cameraError ? (
                       <>
                         <p style={{color: '#ff6b6b', marginBottom: '10px', whiteSpace: 'pre-line'}}>❌ {cameraError}</p>
                         <div style={{display: 'flex', gap: '10px', marginBottom: '10px'}}>
                           <button 
                             className="test_camera_btn"
                             onClick={initializeCamera}
                             disabled={loading}
                             style={{
                               padding: '8px 16px',
                               backgroundColor: '#1877f2',
                               color: 'white',
                               border: 'none',
                               borderRadius: '8px',
                               cursor: 'pointer',
                               fontSize: '14px'
                             }}
                           >
                             🔄 Try Again
                           </button>
                           <button 
                             className="test_camera_btn"
                             onClick={runCameraDiagnostic}
                             style={{
                               padding: '8px 16px',
                               backgroundColor: '#42b883',
                               color: 'white',
                               border: 'none',
                               borderRadius: '8px',
                               cursor: 'pointer',
                               fontSize: '14px'
                             }}
                           >
                             🔍 Diagnose
                           </button>
                           <button 
                             className="test_camera_btn"
                             onClick={async () => {
                               try {
                                 console.log('Testing basic camera access...');
                                 const testStream = await navigator.mediaDevices.getUserMedia({ video: true });
                                 console.log('Basic camera test: SUCCESS');
                                 testStream.getTracks().forEach(track => track.stop());
                                 showSuccess('Camera test successful! Try the main "Try Again" button.');
                               } catch (error) {
                                 console.error('Basic camera test failed:', error);
                                 showError(`Camera test failed: ${error.name} - ${error.message}`);
                               }
                             }}
                             style={{
                               padding: '8px 16px',
                               backgroundColor: '#e67e22',
                               color: 'white',
                               border: 'none',
                               borderRadius: '8px',
                               cursor: 'pointer',
                               fontSize: '14px'
                             }}
                           >
                             🧪 Test
                           </button>
                         </div>
                         <p style={{fontSize: '11px', opacity: 0.6, marginTop: '5px', maxWidth: '280px'}}>
                           💡 Click "Diagnose" to check your camera system, then check browser console (F12) for detailed results
                         </p>
                       </>
                     ) : (
                       <>
                         <p>Camera setup complete!</p>
                         <div>✅</div>
                         <p style={{fontSize: '12px', opacity: 0.7, marginTop: '10px'}}>
                           Video should appear behind this message. If not, click refresh below.
                         </p>
                         <button 
                           className="test_camera_btn"
                           onClick={forceVideoRefresh}
                           style={{
                             padding: '8px 16px',
                             backgroundColor: '#1877f2',
                             color: 'white',
                             border: 'none',
                             borderRadius: '8px',
                             cursor: 'pointer',
                             fontSize: '14px',
                             marginTop: '10px'
                           }}
                         >
                           🔄 Refresh Video
                         </button>
                       </>
                     )}
                   </div>
                 </div>
               )}
               
               {/* Debug overlay - always show when camera is ready */}
               {cameraReady && (
                 <>
                   <div style={{
                     position: 'absolute',
                     top: '10px',
                     left: '10px',
                     fontSize: '10px',
                     color: 'white',
                     backgroundColor: 'rgba(0,0,0,0.7)',
                     padding: '4px 8px',
                     borderRadius: '4px',
                     fontFamily: 'monospace',
                     zIndex: 3
                   }}>
     
                   </div>
                   
                   <div style={{
                     position: 'absolute',
                     bottom: '10px',
                     right: '10px',
                     display: 'flex',
                     gap: '5px',
                     zIndex: 3
                   }}>
                     <button
                       onClick={forceVideoRefresh}
                       style={{
                         padding: '4px 8px',
                         fontSize: '12px',
                         backgroundColor: 'rgba(0,0,0,0.7)',
                         color: 'white',
                         border: 'none',
                         borderRadius: '4px',
                         cursor: 'pointer'
                       }}
                       title="Refresh video if not showing"
                     >
                       🔄
                     </button>
                   </div>
                 </>
                              )}
             </div>
            {isLive && (
              <div className="stream_info_overlay">
                <div className="viewer_count">
                  👥 {viewerCount} viewer{viewerCount !== 1 ? 's' : ''}
                </div>
                <div className="stream_duration">
                  ⏱️ {duration}
                </div>
              </div>
            )}
          </div>

          {isLive && allowComments && (
            <div className="broadcaster_live_comments">
              <div className="broadcaster_live_comments_title">
                Live Comments ({liveComments.length})
              </div>
              <div className="broadcaster_live_comments_list">
                {liveComments.length === 0 ? (
                  <div className="broadcaster_live_comment_empty">No comments yet...</div>
                ) : (
                  liveComments.map((comment) => (
                    <div
                      key={comment._id || `${comment.user?._id}-${comment.createdAt}`}
                      className="broadcaster_live_comment_item"
                    >
                      <img
                        src={comment.user?.picture || '/images/default_pic.png'}
                        alt={comment.user?.first_name || 'user'}
                        className="broadcaster_live_comment_avatar"
                      />
                      <div className="broadcaster_live_comment_body">
                        <div className="broadcaster_live_comment_meta">
                          <span className="broadcaster_live_comment_name">
                            {comment.user?.first_name || ''} {comment.user?.last_name || ''}
                          </span>
                          <span className="broadcaster_live_comment_time">
                            {formatCommentTime(comment.createdAt)}
                          </span>
                        </div>
                        <div className="broadcaster_live_comment_text">{comment.text}</div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
          
          {/* Stream Configuration */}
          {!isLive && (
            <div className="stream_config">
              {!cameraReady && (
                <div className="help_text">
                  <p>📝 Setting up your camera, then fill in your stream details below.</p>
                </div>
              )}
              <div className="config_section">
                <label htmlFor="stream_title">Stream Title *</label>
                <input
                  id="stream_title"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="What are you going live about?"
                  maxLength={100}
                />
              </div>
              
              <div className="config_section">
                <label htmlFor="stream_description">Description</label>
                <textarea
                  id="stream_description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Tell viewers what to expect..."
                  rows={3}
                  maxLength={500}
                />
              </div>
              
              <div className="config_row">
                <div className="config_section">
                  <label htmlFor="visibility">Who can see this?</label>
                  <select
                    id="visibility"
                    value={visibility}
                    onChange={(e) => setVisibility(e.target.value)}
                  >
                    <option value="followers">Followers</option>
                    <option value="friends">Friends</option>
                    <option value="public">Public</option>
                  </select>
                </div>
                
                <div className="config_section">
                  <label className="checkbox_label">
                    <input
                      type="checkbox"
                      checked={allowComments}
                      onChange={(e) => setAllowComments(e.target.checked)}
                    />
                    Allow comments
                  </label>
                </div>
              </div>
            </div>
          )}
          
          {/* Stream Controls */}
          <div className="stream_controls">
            {!isLive ? (
              <button
                className="start_stream_btn"
                onClick={startLiveStream}
                disabled={loading || !title.trim() || !cameraReady}
                title={!cameraReady ? 'Setting up camera...' : ''}
              >
                {loading ? 'Starting...' : 
                 !cameraReady ? '⏳ Setting up...' : '🔴 Go Live'}
              </button>
            ) : (
              <button
                className="stop_stream_btn"
                onClick={showEndConfirmationDialog}
                disabled={loading}
              >
                {loading ? 'Stopping...' : '⏹️ End Stream'}
              </button>
            )}
          </div>
        </div>
      </div>
      
      {/* End Stream Confirmation Dialog */}
      {showEndConfirmation && (
        <div className="confirmation_overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div className="confirmation_modal" style={{
            background: 'var(--bg-primary)',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: '48px',
              marginBottom: '16px'
            }}>
              ⚠️
            </div>
            
            <h3 style={{
              margin: '0 0 16px 0',
              color: 'var(--color-primary)',
              fontSize: '18px',
              fontWeight: '600'
            }}>
              End Live Stream?
            </h3>
            
            <p style={{
              margin: '0 0 20px 0',
              color: 'var(--color-secondary)',
              fontSize: '14px',
              lineHeight: '1.5'
            }}>
              This will disconnect all viewers and end your stream permanently.
            </p>
            
            <div style={{
              background: 'var(--bg-secondary)',
              padding: '12px',
              borderRadius: '8px',
              marginBottom: '20px',
              fontSize: '13px',
              color: 'var(--color-secondary)'
            }}>
              <div style={{ marginBottom: '4px' }}>
                <strong>Current Duration:</strong> {duration}
              </div>
              <div>
                <strong>Current Viewers:</strong> {viewerCount} viewer{viewerCount !== 1 ? 's' : ''}
              </div>
            </div>
            
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'center'
            }}>
              <button
                onClick={() => setShowEndConfirmation(false)}
                style={{
                  padding: '10px 20px',
                  background: 'var(--bg-third)',
                  border: 'none',
                  borderRadius: '6px',
                  color: 'var(--color-primary)',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Cancel
              </button>
              
              <button
                onClick={stopLiveStream}
                disabled={loading}
                style={{
                  padding: '10px 20px',
                  background: loading ? '#95a5a6' : '#e74c3c',
                  border: 'none',
                  borderRadius: '6px',
                  color: 'white',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                {loading ? 'Ending...' : 'End Stream'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 
